import { Tick } from '@/components/icons';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * One pickable row in a list of answers.
 *
 * `control` is what the choice actually is, not how it looks: `checkbox` for a
 * list where several answers can be true at once, `radio` for one where exactly
 * one is. The glyph follows from it, and so does the role assistive tech reads —
 * the two used to drift apart, with mutually exclusive choices announced as
 * checkboxes.
 *
 * Radio groups need a `role="radiogroup"` wrapper with an accessible name; the
 * caller owns that, because the name is the question being asked.
 */
export interface SelectableOptionProps {
    control: 'checkbox' | 'radio';
    checked: boolean;
    onToggle: () => void;
    label: ReactNode;
    description?: ReactNode;
    disabled?: boolean;
    className?: string;
}

export function SelectableOption({
    control,
    checked,
    onToggle,
    label,
    description,
    disabled,
    className,
}: SelectableOptionProps) {
    const isRadio = control === 'radio';

    return (
        <button
            type="button"
            role={control}
            aria-checked={checked}
            disabled={disabled}
            onClick={onToggle}
            className={cn(
                'flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'disabled:cursor-not-allowed disabled:opacity-60',
                checked ? 'border-brand/40 bg-brand-subtle' : 'border-border hover:bg-surface',
                className,
            )}
        >
            <span
                aria-hidden="true"
                className={cn(
                    'mt-0.5 flex size-4 shrink-0 items-center justify-center border transition-colors',
                    isRadio ? 'rounded-full' : 'rounded-sm',
                    checked
                        ? 'border-brand bg-brand text-primary-foreground'
                        : 'border-border-strong bg-card text-transparent',
                )}
            >
                {isRadio ? (
                    <span className="size-1.5 rounded-full bg-current" />
                ) : (
                    <Tick className="size-3" />
                )}
            </span>

            <span className="min-w-0 space-y-1">
                <span className="block text-sm text-foreground">{label}</span>
                {description && (
                    <span className="block text-xs text-muted-foreground">{description}</span>
                )}
            </span>
        </button>
    );
}
